import { MediaService } from "../../../services/media.service.js";

export class WebnovelSidebar {
    constructor(container, props) {
        this.container = container;
        this.props = props; // { data, onUpdate, onSelectChapter, activeIndex, onAddChapter, onDeleteChapter, onSave }
        this.sortable = null;
    }

    render() {
        this.container.innerHTML = `
            <aside class="w-80 flex-shrink-0 flex flex-col border-r border-white/10 bg-black/20 h-full backdrop-blur-sm">
                <!-- Series Metadata -->
                <div class="p-4 border-b border-white/5 overflow-y-auto max-h-[40vh] custom-scrollbar">
                     <label class="block text-[10px] font-bold text-indigo-400 uppercase tracking-wider mb-2">${window.locale.t('editor.series_info_upload')}</label>
                     
                     <div class="space-y-3">
                        <input class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-2 text-xs text-white focus:border-indigo-500 transition-colors" id="novel-title" type="text" value="${this.props.data.title || ''}" placeholder="${window.locale.t('editor.series_title')}">
                        
                        <div class="flex items-center gap-2">
                            <input class="flex-grow bg-zinc-800/50 border border-white/10 rounded-lg p-2 text-xs text-zinc-300 font-mono focus:border-indigo-500 transition-colors" id="novel-cover" type="text" value="${this.props.data.coverImage || ''}" placeholder="${window.locale.t('editor.cover_url')}">
                            <label class="cursor-pointer w-8 h-8 flex-shrink-0 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-lg border border-white/10 text-zinc-400 hover:text-white transition-colors">
                                <span class="material-symbols-outlined text-sm">upload</span>
                                <input type="file" id="novel-cover-upload" class="hidden" accept="image/*">
                            </label>
                        </div>

                        <input class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-2 text-xs text-white focus:border-indigo-500 transition-colors" id="novel-tags" type="text" value="${(this.props.data.tags || []).join(', ')}" placeholder="${window.locale.t('editor.tags')}">
                        
                        <!-- AI Checkbox -->
                        <div class="flex items-center gap-2 mt-1">
                            <input type="checkbox" id="novel-is-ai" class="w-3 h-3 rounded border-zinc-600 text-indigo-600 focus:ring-indigo-500 bg-zinc-800" ${this.props.data.isAI ? 'checked' : ''}>
                            <label for="novel-is-ai" class="text-[10px] text-zinc-400 select-none cursor-pointer flex items-center gap-1">
                                <span class="material-symbols-outlined text-[12px]">smart_toy</span>
                                ${window.locale.t('editor.is_ai')}
                            </label>
                        </div>

                        <!-- Series Status -->
                        <div class="mt-3 bg-zinc-800/50 rounded-lg p-2 border border-white/5">
                            <label class="block text-[10px] text-zinc-500 font-bold uppercase mb-1">${window.locale.t('editor.series_status')}</label>
                            <div class="flex items-center gap-1 bg-black/40 rounded p-1">
                                <button id="status-draft" class="flex-1 py-1.5 text-xs font-bold rounded text-zinc-400 hover:text-white hover:bg-white/5 transition-all ${this.props.data.status === 'draft' ? 'bg-zinc-700 text-white shadow' : ''}">
                                    <span class="flex items-center justify-center gap-1"><span class="material-symbols-outlined text-[12px]">lock</span> ${window.locale.t('common.draft')}</span>
                                </button>
                                <button id="status-published" class="flex-1 py-1.5 text-xs font-bold rounded text-zinc-400 hover:text-white hover:bg-white/5 transition-all ${this.props.data.status === 'published' ? 'bg-indigo-600 text-white shadow' : ''}">
                                    <span class="flex items-center justify-center gap-1"><span class="material-symbols-outlined text-[12px]">public</span> ${window.locale.t('common.published')}</span>
                                </button>
                            </div>
                        </div>

                        <!-- Admin Fields -->
                        ${this.renderMetaFields()}
                     </div>
                </div>

                <!-- Chapter List Header -->
                <div class="px-4 py-2 border-b border-white/5 bg-white/5 flex items-center justify-between">
                     <span class="text-[10px] font-bold text-zinc-400 uppercase">${window.locale.t('editor.chapters')} (${this.props.data.chapters.length})</span>
                     <button id="btn-add-chapter" class="w-6 h-6 flex items-center justify-center rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors" title="${window.locale.t('editor.add_chapter')}">
                        <span class="material-symbols-outlined text-sm">add</span>
                     </button>
                </div>

                <!-- Sortable List -->
                <div id="chapter-list" class="flex-grow overflow-y-auto custom-scrollbar p-2 space-y-1">
                    <!-- Items injected here -->
                </div>
            </aside>
        `;

        this.renderChapterList();
        this.bindEvents();
    }

    renderMetaFields() {
        const isAdmin = window.user && (window.user.isSuperAdmin || ['admin', 'super_admin'].includes(window.user.role));
        if (!isAdmin) return '';

        return `
            <div class="mt-2 pt-2 border-t border-white/5 bg-yellow-500/5 p-2 rounded-lg border border-yellow-500/10">
                <div class="text-[9px] font-bold text-yellow-500 uppercase mb-1 flex items-center gap-1">
                     <span class="material-symbols-outlined text-[10px]">lock</span> ${window.locale.t('editor.admin_info')}
                </div>
                <div class="grid grid-cols-2 gap-2">
                    <input class="w-full bg-zinc-900/50 border border-zinc-700/30 rounded p-1.5 text-[10px] text-zinc-400 font-mono" id="novel-isbn" type="text" value="${this.props.data.isbn || ''}" placeholder="ISBN" readonly>
                    <input class="w-full bg-zinc-900/50 border border-zinc-700/30 rounded p-1.5 text-[10px] text-zinc-400 font-mono" id="novel-uci" type="text" value="${this.props.data.uci || ''}" placeholder="UCI" readonly>
                </div>
            </div>
        `;
    }

    renderChapterList() {
        const listEl = this.container.querySelector('#chapter-list');
        listEl.innerHTML = '';

        this.props.data.chapters.forEach((ch, idx) => {
            const isActive = idx === this.props.activeIndex;
            const item = document.createElement('div');
            item.className = `group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all border ${isActive ? 'bg-indigo-600/20 border-indigo-500/50' : 'bg-transparent border-transparent hover:bg-white/5'}`;
            item.dataset.index = idx;

            item.innerHTML = `
                <div class="drag-handle cursor-grab active:cursor-grabbing text-zinc-600 group-hover:text-zinc-400">
                    <span class="material-symbols-outlined text-sm">drag_indicator</span>
                </div>
                <div class="flex-grow min-w-0">
                    <div class="flex items-center gap-2">
                        <span class="text-xs font-bold ${isActive ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-300'} truncate">
                            ${ch.title || window.locale.t('editor.untitled_chapter')}
                        </span>
                        ${ch.status === 'draft' ? `<span class="material-symbols-outlined text-[12px] text-zinc-500" title="${window.locale.t('editor.status_draft')}">lock</span>` : ''}
                    </div>
                    <div class="text-[10px] text-zinc-600 truncate font-mono">
                        ${(ch.content || '').length.toLocaleString()} chars
                    </div>
                </div>
                <button class="delete-btn opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all">
                    <span class="material-symbols-outlined text-sm">close</span>
                </button>
            `;

            // Click to select
            item.onclick = (e) => {
                if (e.target.closest('.delete-btn')) return;
                this.props.onSelectChapter(idx);
            };

            // Delete logic
            const delBtn = item.querySelector('.delete-btn');
            if (delBtn) {
                delBtn.onclick = (e) => {
                    e.stopPropagation();
                    this.props.onDeleteChapter(idx);
                };
            }

            listEl.appendChild(item);
        });

        // Initialize Sortable
        if (this.sortable) this.sortable.destroy();
        this.sortable = new window.Sortable(listEl, {
            animation: 150,
            handle: '.drag-handle',
            ghostClass: 'bg-indigo-500/10',
            onEnd: (evt) => {
                this.props.onReorder(evt.oldIndex, evt.newIndex);
            }
        });
    }

    bindEvents() {
        // Series Meta Inputs
        this.container.querySelector('#novel-title').oninput = (e) => {
            this.props.data.title = e.target.value;
            this.props.onUpdate(); // Trigger Auto-save
        };

        this.container.querySelector('#novel-cover').oninput = (e) => {
            this.props.data.coverImage = e.target.value;
            this.props.onUpdate();
        };

        this.container.querySelector('#novel-tags').oninput = (e) => {
            this.props.data.tags = e.target.value.split(',').map(t => t.trim());
            this.props.onUpdate();
        };

        this.container.querySelector('#novel-is-ai').onchange = (e) => {
            this.props.data.isAI = e.target.checked;
            this.props.onUpdate();
        };

        // Status Toggles
        this.container.querySelector('#status-draft').onclick = () => {
            this.props.data.status = 'draft';
            this.render(); // Re-render to update button styles
            this.props.onUpdate(); // Trigger Auto-save
        };

        this.container.querySelector('#status-published').onclick = () => {
            this.props.data.status = 'published';
            this.render();
            this.props.onUpdate();
        };

        this.container.querySelector('#btn-add-chapter').onclick = () => {
            this.props.onAddChapter();
        };

        // Cover Upload
        const coverInput = this.container.querySelector('#novel-cover-upload');
        if (coverInput) {
            coverInput.onchange = async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                try {
                    const btn = coverInput.parentElement;
                    const originalHTML = btn.innerHTML;
                    btn.innerHTML = '<span class="material-symbols-outlined animate-spin text-sm">progress_activity</span>';

                    const compressed = await MediaService.compressImage(file);
                    // Generate safe path (assuming seriesId is in logic, but props logic needed)
                    // We might need to ask parent to upload or pass seriesId to Sidebar
                    // Let's pass 'uploadImage' callback or assume MediaService works

                    if (this.props.onUploadCover) {
                        const url = await this.props.onUploadCover(compressed);
                        this.container.querySelector('#novel-cover').value = url;
                        this.props.data.coverImage = url;
                    }

                    btn.innerHTML = '<span class="material-symbols-outlined text-sm">check</span>';
                    setTimeout(() => {
                        this.render(); // Re-render to restore input
                    }, 1000);
                } catch (err) {
                    console.error(err);
                    window.Modal.alert("Cover Upload Failed: " + err.message);
                }
            };
        }
    }
}
